import { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { getAdminVacancyDetailApi, AdminVacancyDetail } from "../../features/analytics/api/adminAnalytics.api"
import { LineChart, ChartCard } from "./Charts"
import { ChevronLeftIcon, ExclamationTriangleIcon } from "@heroicons/react/16/solid"

const StatCard = ({ label, value, sub }: { label: string; value: string | number; sub?: string }) => (
  <div className="bg-white border border-gray-100 rounded-2xl px-5 py-4">
    <p className="text-xs text-gray-400 mb-1">{label}</p>
    <p className="text-2xl font-bold text-gray-900">{typeof value === "number" ? value.toLocaleString("ru") : value}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
)

export const VacancyDetailPage = () => {
  const { vacancyId } = useParams<{ vacancyId: string }>()
  const [detail, setDetail] = useState<AdminVacancyDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const load = () => {
    if (!vacancyId) return
    setLoading(true)
    setError(false)
    getAdminVacancyDetailApi(vacancyId)
      .then((res) => setDetail(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [vacancyId]) // eslint-disable-line

  if (error) {
    return (
      <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
        <ExclamationTriangleIcon className="w-8 h-8 text-red-200 mx-auto mb-2" />
        <p className="text-sm text-gray-400 mb-2">Не удалось загрузить вакансию</p>
        <button onClick={load} className="text-xs font-medium text-blue-600 hover:text-blue-700 underline">
          Повторить
        </button>
      </div>
    )
  }

  if (loading || !detail) {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[1, 2, 3].map((i) => <div key={i} className="bg-white border border-gray-100 rounded-2xl h-20 animate-pulse" />)}
        </div>
        <div className="bg-white border border-gray-100 rounded-2xl h-48 animate-pulse" />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3">
        <Link to="/admin/analytics/vacancies"
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-600 transition-colors">
          <ChevronLeftIcon className="w-4 h-4" />
          Вакансии
        </Link>
        <span className="text-gray-300">/</span>
        <h2 className="text-base font-semibold text-gray-800 truncate">{detail.title}</h2>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatCard label="Просмотров" value={detail.views} />
        <StatCard label="Заявок" value={detail.applications} />
        <StatCard label="Конверсия" value={`${detail.application_rate}%`} />
      </div>

      {/* Applications by day */}
      <ChartCard title="Заявки по дням" note="Просмотры вакансии по дням не трекаются, только отклики">
        {detail.applications_by_day.length > 0 ? (
          <LineChart
            data={detail.applications_by_day.map((r) => ({ label: r.date, value: r.count }))}
            color="#10b981"
          />
        ) : (
          <div className="flex items-center justify-center h-40">
            <p className="text-sm text-gray-300">Нет данных</p>
          </div>
        )}
      </ChartCard>
    </div>
  )
}
